import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { StudioRenewalRailKey } from "./studio-authority-workspaces";

type RailLabels = Record<StudioRenewalRailKey, string>;

const EN: RailLabels = {
  "app.preview": "Preview",
  "app.pages": "Pages",
  "app.action": "Behavior",
  "app.data": "Data",
  "app.check": "Review",
  "app.publish": "Publish",
  "app.launch": "Launch",
  "app.settings": "Settings",
  "document.preview": "Preview",
  "document.sections": "Sections",
  "document.body": "Body",
  "document.sources": "Sources",
  "document.export": "Export",
  "document.settings": "Settings",
  "game.preview": "Preview",
  "game.scenes": "Scenes",
  "game.rules": "Rules",
  "game.data": "Data",
  "game.check": "Checks",
  "game.publish": "Publish",
  "game.launch": "Launch",
  "game.settings": "Settings",
  "design.canvas": "Canvas",
  "design.tokens": "Tokens",
  "design.components": "Components",
  "design.settings": "Settings",
  "guide.body": "Content",
  "guide.sections": "Sections",
  "guide.style": "Styles",
  "guide.settings": "Settings",
  "insights.overview": "Status",
  "insights.coverage": "Scope",
  'insights.records': 'Export',
  'insights.quality': 'Checks',
  "insights.settings": "Settings",
};

const KO: RailLabels = {
  "app.preview": "미리보기",
  "app.pages": "페이지",
  "app.action": "동작",
  "app.data": "데이터",
  "app.check": "확인",
  "app.publish": "게시",
  "app.launch": "출시",
  "app.settings": "설정",
  "document.preview": "미리보기",
  "document.sections": "섹션",
  "document.body": "본문",
  "document.sources": "출처",
  "document.export": "내보내기",
  "document.settings": "설정",
  "game.preview": "미리보기",
  "game.scenes": "장면",
  "game.rules": "규칙",
  "game.data": "데이터",
  "game.check": "확인",
  "game.publish": "게시",
  "game.launch": "출시",
  "game.settings": "설정",
  "design.canvas": "캔버스",
  "design.tokens": "토큰",
  "design.components": "컴포넌트",
  "design.settings": "설정",
  "guide.body": "본문",
  "guide.sections": "섹션",
  "guide.style": "스타일",
  "guide.settings": "설정",
  "insights.overview": "현황",
  "insights.coverage": "범위",
  'insights.records': '내보내기',
  'insights.quality': '검사',
  "insights.settings": "설정",
};

const JA: RailLabels = {
  "app.preview": "プレビュー",
  "app.pages": "ページ",
  "app.action": "動作",
  "app.data": "データ",
  "app.check": "確認",
  "app.publish": "公開",
  "app.launch": "リリース",
  "app.settings": "設定",
  "document.preview": "プレビュー",
  "document.sections": "セクション",
  "document.body": "本文",
  "document.sources": "出典",
  "document.export": "書き出し",
  "document.settings": "設定",
  "game.preview": "プレビュー",
  "game.scenes": "シーン",
  "game.rules": "ルール",
  "game.data": "データ",
  "game.check": "確認",
  "game.publish": "公開",
  "game.launch": "リリース",
  "game.settings": "設定",
  "design.canvas": "キャンバス",
  "design.tokens": "トークン",
  "design.components": "コンポーネント",
  "design.settings": "設定",
  "guide.body": "本文",
  "guide.sections": "セクション",
  "guide.style": "スタイル",
  "guide.settings": "設定",
  "insights.overview": "状況",
  "insights.coverage": "範囲",
  'insights.records': '書き出し',
  'insights.quality': '確認',
  "insights.settings": "設定",
};

const ZH: RailLabels = {
  "app.preview": "预览",
  "app.pages": "页面",
  "app.action": "动作",
  "app.data": "数据",
  "app.check": "确认",
  "app.publish": "发布",
  "app.launch": "上线",
  "app.settings": "设置",
  "document.preview": "预览",
  "document.sections": "章节",
  "document.body": "正文",
  "document.sources": "来源",
  "document.export": "导出",
  "document.settings": "设置",
  "game.preview": "预览",
  "game.scenes": "场景",
  "game.rules": "规则",
  "game.data": "数据",
  "game.check": "确认",
  "game.publish": "发布",
  "game.launch": "上线",
  "game.settings": "设置",
  "design.canvas": "画布",
  "design.tokens": "令牌",
  "design.components": "组件",
  "design.settings": "设置",
  "guide.body": "正文",
  "guide.sections": "章节",
  "guide.style": "样式",
  "guide.settings": "设置",
  "insights.overview": "现状",
  "insights.coverage": "范围",
  'insights.records': '导出',
  'insights.quality': '检查',
  "insights.settings": "设置",
};

const ZH_TW: RailLabels = {
  ...ZH,
  "app.preview": "預覽",
  "app.pages": "頁面",
  "app.action": "動作",
  "app.data": "資料",
  "app.check": "確認",
  "app.publish": "發佈",
  "app.launch": "上線",
  "app.settings": "設定",
  "document.preview": "預覽",
  "document.sections": "章節",
  "document.body": "正文",
  "document.sources": "來源",
  "document.export": "匯出",
  "document.settings": "設定",
  "game.preview": "預覽",
  "game.scenes": "場景",
  "game.rules": "規則",
  "game.data": "資料",
  "game.check": "確認",
  "game.publish": "發佈",
  "game.launch": "上線",
  "game.settings": "設定",
  "design.canvas": "畫布",
  "design.tokens": "權杖",
  "design.components": "元件",
  "design.settings": "設定",
  "guide.body": "正文",
  "guide.sections": "章節",
  "guide.style": "樣式",
  "guide.settings": "設定",
  "insights.overview": "現況",
  "insights.coverage": "範圍",
  'insights.records': '匯出',
  'insights.quality': '檢查',
  "insights.settings": "設定",
};

const LABELS: Record<LocaleCode, RailLabels> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  "zh-TW": ZH_TW,
};

export function getCoogoIdeRailLabel(
  locale: LocaleCode,
  key: StudioRenewalRailKey,
): string {
  return LABELS[locale][key];
}

export function getCoogoNeedsReviewLabel(
  locale: LocaleCode,
  count: number,
): string {
  switch (locale) {
    case "ko":
      return `내 확인 ${count}`;
    case "ja":
      return `確認 ${count}`;
    case "zh":
      return `待确认 ${count}`;
    case "zh-TW":
      return `待確認 ${count}`;
    default:
      return `${count} to review`;
  }
}

export function getCoogoInsightsArtifactLabel(locale: LocaleCode): string {
  switch (locale) {
    case "ko":
      return "현황";
    case "ja":
      return "状況";
    case "zh":
      return "现状";
    case "zh-TW":
      return "現況";
    default:
      return "Status";
  }
}

/**
 * The As-Is tab label (G1 alternative A) — shown only when a conversion
 * project has a snapshot.
 *
 * Without `hasSourceMarkup` (the default), NOT "원본" / "Original"
 * (FINAL-REVIEW, overclaim): what the tab opens is a STRUCTURE READ OUT of
 * the original — field names lifted from HTML `name` attributes, `<th>`
 * texts, and button labels, in hand-written containers. No layout, styling,
 * data, or behaviour travels with it (`legacy-screen-as-is-capture.ts`'s
 * honesty boundary). A label promising "the original" makes a bank developer
 * read the readback as a broken import; this one promises what is actually
 * there.
 *
 * With `hasSourceMarkup: true` (task-4 fix-round-2), the claim flips because
 * what is rendered flips: `AsIsSnapshot.sourceMarkup` present means this tab
 * now shows the pasted original's own bytes, not a reading of it, so it says
 * so — reusing the SAME word `getCoogoAsIsPaneCopy(locale).asIsSourceTruthHeading`
 * gives the pane, so the tab and the pane can never say two different things
 * about the same snapshot. Callers MUST derive `hasSourceMarkup` from
 * `hasAsIsSourceMarkup` (as-is-snapshot.ts) rather than re-deciding it —
 * that single predicate is what keeps the pane, the tab, and the chat rail
 * (studio-authority-copy.ts's `getStudioAuthorityTaskCopy`) from drifting
 * apart again.
 */
export function getCoogoAsIsArtifactLabel(
  locale: LocaleCode,
  hasSourceMarkup = false,
): string {
  if (hasSourceMarkup) return getCoogoAsIsPaneCopy(locale).asIsSourceTruthHeading;
  switch (locale) {
    case "ko":
      return "원본 구성";
    case "ja":
      return "元の構成";
    case "zh":
      return "原始结构";
    case "zh-TW":
      return "原始結構";
    default:
      return "Original structure";
  }
}

export interface CoogoAsIsPaneCopy {
  /** The pane heading — says the artifact is a reading, not a reproduction. */
  heading: string;
  /**
   * The line that keeps the pane honest: what was NOT carried over. Shown
   * unconditionally, because the omissions are not visible from the render
   * (an empty-looking screen reads as a failed import otherwise).
   */
  fidelityNote: string;
  /**
   * Shown while the As-Is chunk loads. `fallback={null}` twice over (shell
   * boundary + the view's own AppV2 boundary) read as two sequential blank
   * panes — indistinguishable from "there is nothing here".
   */
  loading: string;
  /**
   * Heading used when `AsIsSnapshot.sourceMarkup` is present — the pane is
   * now showing the pasted original's own bytes in a sandboxed frame, not a
   * structure read out of it. Deliberately shorter/plainer than `heading`
   * ("원본"/"Original") because the claim it makes is stronger: this is not a
   * reading of the original, it IS the original (minus the elements the
   * sanitizer removed for safety — see `asIsRemovedNote`).
   */
  asIsSourceTruthHeading: string;
  /**
   * Shown instead of `fidelityNote` when rendering the pasted original
   * verbatim: explains what the sandboxed frame does NOT do (load external
   * styles/images, see server data — so a `${...}` placeholder shows up as
   * plain text instead of a real value — run scripts) so the user reads
   * that as expected, not as a bug. Design spec §6
   * (docs/superpowers/specs/2026-08-12-as-is-markup-fidelity-design.md:91)
   * requires the `${...}`-literal clause specifically: it is the single
   * most predictable confusion in the feature (a pasted JSP shows
   * `${account.balance}` where a number should be).
   */
  asIsMarkupNote: string;
  /**
   * Count of elements the sanitizer stripped for safety before the markup
   * reached this pane. Contains a literal `{count}` placeholder the caller
   * substitutes (mirrors `AsIsSnapshot.sourceMarkup.removed`).
   */
  asIsRemovedNote: string;
  /**
   * `loading`'s source-truth counterpart (task-4 fix-round-4). The chunk-wait
   * line is the FIRST thing shown when the As-Is tab is opened, before the
   * lazily-imported pane lands — so on a `sourceMarkup` project the derived
   * `loading` wording announced "the original structure" and the pane then
   * corrected itself to "the original" a moment later. Uses the same word as
   * `asIsSourceTruthHeading`, so the wait line cannot promise something the
   * pane contradicts.
   */
  asIsSourceTruthLoading: string;
}

export function getCoogoAsIsPaneCopy(locale: LocaleCode): CoogoAsIsPaneCopy {
  switch (locale) {
    case "ko":
      return {
        heading: "원본에서 읽어낸 구성",
        fidelityNote:
          "레이아웃·스타일·데이터·동작은 재현하지 않습니다. 항목 이름은 원본 화면이 쓰던 필드 이름을 그대로 옮긴 것입니다.",
        loading: "원본 구성을 불러오는 중…",
        asIsSourceTruthHeading: "원본",
        asIsMarkupNote:
          "붙여넣은 원본 그대로입니다. 외부 스타일·이미지는 불러오지 않고, 서버 데이터가 없어 `${...}` 같은 자리표시자는 글자 그대로 보이며, 동작(스크립트)은 실행되지 않습니다.",
        asIsRemovedNote: "안전을 위해 제거된 요소 {count}건",
        asIsSourceTruthLoading: "원본을 불러오는 중…",
      };
    case "ja":
      return {
        heading: "元画面から読み取った構成",
        fidelityNote:
          "レイアウト・スタイル・データ・動作は再現していません。項目名は元画面のフィールド名をそのまま引き継いだものです。",
        loading: "元の構成を読み込んでいます…",
        asIsSourceTruthHeading: "原本",
        asIsMarkupNote:
          "貼り付けた原文のままです。外部のスタイル・画像は読み込まれず、サーバー側のデータがないため `${...}` のようなプレースホルダーはそのまま文字として表示され、動作（スクリプト）も実行されません。",
        asIsRemovedNote: "安全のため削除された要素 {count} 件",
        asIsSourceTruthLoading: "原本を読み込んでいます…",
      };
    case "zh":
      return {
        heading: "从原始页面读取的结构",
        fidelityNote:
          "不还原布局、样式、数据和行为。条目名称直接沿用原始页面使用的字段名。",
        loading: "正在加载原始结构…",
        asIsSourceTruthHeading: "原文",
        asIsMarkupNote:
          "这是粘贴的原始内容本身。不会加载外部样式和图片；由于没有服务器端数据，`${...}` 这样的占位符会原样显示为文字，脚本也不会执行。",
        asIsRemovedNote: "为安全起见移除了 {count} 个元素",
        asIsSourceTruthLoading: "正在加载原文…",
      };
    case "zh-TW":
      return {
        heading: "從原始頁面讀取的結構",
        fidelityNote:
          "不還原版面、樣式、資料與行為。項目名稱直接沿用原始頁面使用的欄位名稱。",
        loading: "正在載入原始結構…",
        asIsSourceTruthHeading: "原文",
        asIsMarkupNote:
          "這是貼上的原始內容本身。不會載入外部樣式與圖片；由於沒有伺服器端資料，`${...}` 這樣的預留位置會原樣顯示為文字，指令碼也不會執行。",
        asIsRemovedNote: "為安全起見移除了 {count} 個元素",
        asIsSourceTruthLoading: "正在載入原文…",
      };
    default:
      return {
        heading: "Structure read from the original",
        fidelityNote:
          "Layout, styling, data, and behaviour are not reproduced. Item names are the field names the original used, carried over unchanged.",
        loading: "Loading the original structure…",
        asIsSourceTruthHeading: "Original",
        asIsMarkupNote:
          "This is exactly what was pasted in. External styles and images are not loaded; because there is no server data, a placeholder like `${...}` shows up as plain text instead of a real value, and scripts do not run.",
        asIsRemovedNote: "{count} element(s) removed for safety",
        asIsSourceTruthLoading: "Loading the original…",
      };
  }
}

/**
 * A localized word for `AsIsSnapshot.sourceKind`.
 *
 * Returns null for a kind this copy does not know — the pane then says
 * nothing rather than printing an internal enum (`legacy-screen`) at the user
 * (FINAL-REVIEW M3).
 */
export function getCoogoAsIsSourceKindLabel(
  locale: LocaleCode,
  sourceKind: string,
): string | null {
  if (sourceKind !== "legacy-screen") return null;
  switch (locale) {
    case "ko":
      return "레거시 화면";
    case "ja":
      return "レガシー画面";
    case "zh":
      return "旧版页面";
    case "zh-TW":
      return "舊版頁面";
    default:
      return "Legacy screen";
  }
}

export function getCoogoReviewThenPublishLabel(
  locale: LocaleCode,
  count: number,
): string {
  switch (locale) {
    case "ko":
      return `내 확인 ${count} → 발행`;
    case "ja":
      return `確認 ${count} → 公開`;
    case "zh":
      return `待确认 ${count} → 发布`;
    case "zh-TW":
      return `待確認 ${count} → 發佈`;
    default:
      return `Review ${count} → Publish`;
  }
}
